/**
 * Address normalization utilities to prevent duplicate entries
 * and ensure consistent formatting across the application
 */

/**
 * Normalize a string field by trimming whitespace and converting to Title Case
 * Preserves abbreviations like P.O., N.W., etc.
 */
export function normalizeString(value: string | null | undefined): string {
  if (!value || typeof value !== 'string') return '';
  
  return value
    .trim()
    .split(' ')
    .map(word => {
      // Handle abbreviations like P.O., N.W., etc. - preserve uppercase after periods
      if (word.includes('.')) {
        return word.split('.').map(part => 
          part.length > 0 
            ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
            : ''
        ).join('.');
      }
      // Handle hyphenated words like "Kwazulu-Natal"
      if (word.includes('-')) {
        return word.split('-').map(part =>
          part.length > 0
            ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
            : ''
        ).join('-');
      }
      // Standard Title Case for regular words
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Normalize city names with special handling for common abbreviations
 */
export function normalizeCity(city: string | null | undefined): string {
  if (!city) return '';
  
  const normalized = normalizeString(city);
  
  // Handle common abbreviations and variations
  const cityMappings: Record<string, string> = {
    'Cpt': 'Cape Town',
    'Ct': 'Cape Town',
    'Jhb': 'Johannesburg',
    'Jozi': 'Johannesburg',
    'Pta': 'Pretoria',
    'Dbn': 'Durban',
    'Pe': 'Port Elizabeth',
    'Ek': 'East London',
  };
  
  return cityMappings[normalized] || normalized;
}

/**
 * Normalize suburb names
 */
export function normalizeSuburb(suburb: string | null | undefined): string {
  if (!suburb) return '';
  return normalizeString(suburb);
}

/**
 * Normalize province/state names with standard abbreviations
 */
export function normalizeProvinceState(provinceState: string | null | undefined): string {
  if (!provinceState) return '';
  
  const normalized = normalizeString(provinceState);
  
  // Handle South African province abbreviations
  const provinceMappings: Record<string, string> = {
    'Wc': 'Western Cape',
    'Ec': 'Eastern Cape',
    'Nc': 'Northern Cape',
    'Fs': 'Free State',
    'Kzn': 'Kwazulu-Natal',
    'Nw': 'North West',
    'Gp': 'Gauteng',
    'Mp': 'Mpumalanga',
    'Lp': 'Limpopo',
  };
  
  return provinceMappings[normalized] || normalized;
}

/**
 * Normalize postal codes by removing spaces and ensuring consistent format
 */
export function normalizePostalCode(postalCode: string | null | undefined): string {
  if (!postalCode) return '';
  
  return postalCode
    .toString()
    .replace(/\s+/g, '')
    .trim();
}

/**
 * Normalize all address fields of a customer object
 */
export function normalizeCustomerAddress(customer: any): any {
  return {
    ...customer,
    city: normalizeCity(customer.city),
    suburb: normalizeSuburb(customer.suburb),
    province_state: normalizeProvinceState(customer.province_state),
    postal_code: normalizePostalCode(customer.postal_code),
    street_address: normalizeString(customer.street_address),
  };
}